import { useState, type ReactNode } from "react";
import { changeSummary, EXAMPLE_JOB, EXAMPLE_JOB_ASKS, EXAMPLE_RESUME } from "@/lib/example";
import { EXAMPLE_FORM, EXAMPLE_POSTING_ABOUT, type FillMode } from "@/lib/exampleFill";
import { useFreeTailors } from "@/lib/hooks/billing";
import ExampleForm from "./ExampleForm";
import ExampleResume from "./ExampleResume";
import FillModes from "./FillModes";
import JobLine, { CARD } from "./JobLine";
import PlacePreview from "./PlacePreview";

/** The free tailors a month, as the Resume page's note says them; the bare promise until the plans are read. */
function TailorsNote() {
    const tailors = useFreeTailors();
    return (
        <>
            {tailors === null
                ? "Optional AI tailoring: free AI-tailored resumes every month."
                : `Optional AI tailoring: ${tailors} free AI-tailored resumes per month.`}
        </>
    );
}

/** A step of the example, numbered: the posting, the resume, the form. */
function Step({ n, children }: { n: number; children: ReactNode }) {
    return (
        <h2 className="m-0 flex items-center gap-2 text-[12.5px] font-semibold text-fg-muted">
            <span
                className="flex size-5 items-center justify-center rounded-full bg-fg font-mono text-[11px] font-bold text-card"
                aria-hidden="true"
            >
                {n}
            </span>
            {children}
        </h2>
    );
}

/**
 * Resume: how one stored resume fills an application, shown on the
 * example job. The visitor picks a fill, free from the stored resume or
 * with a tailor, and sees the posting, the resume it attaches (Jordan's
 * own, or tailored with each change marked) and the form as it is left.
 */
export default function ResumePreview() {
    const [mode, setMode] = useState<FillMode>("resume");
    const tailored = mode === "tailor";
    return (
        <PlacePreview kind="resume" note={<TailorsNote />}>
            <div className="flex flex-col gap-3">
                <FillModes mode={mode} onMode={setMode} />
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 max-[900px]:grid-cols-1">
                    <aside className={`${CARD} flex flex-col gap-3 self-start px-[18px] py-4`}>
                        <Step n={1}>The posting</Step>
                        <JobLine job={EXAMPLE_JOB} />
                        <p className="m-0 text-[13px] leading-normal">{EXAMPLE_POSTING_ABOUT}</p>
                        <div className="flex flex-col gap-1.5">
                            <p className="m-0 text-[12.5px] text-fg-muted">It asks for</p>
                            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                                {EXAMPLE_JOB_ASKS.map((ask) => (
                                    <li
                                        key={ask}
                                        className="rounded-full border border-line px-2.5 py-[3px] text-[12px] font-medium"
                                    >
                                        {ask}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <p className="m-0 text-[12.5px] text-fg-muted">
                            Its application has {EXAMPLE_FORM.length} fields, three of them written
                            answers.
                        </p>
                    </aside>
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1 px-1.5">
                            <Step n={2}>The resume it attaches</Step>
                            <p className="m-0 text-[13px] text-fg-muted" aria-live="polite">
                                {tailored ? (
                                    <>
                                        {changeSummary(EXAMPLE_RESUME, EXAMPLE_JOB)},{" "}
                                        <mark className="rounded-[4px] bg-tint-green px-1 text-fg">
                                            highlighted
                                        </mark>
                                        . Nothing added that Jordan has not done.
                                    </>
                                ) : (
                                    "The resume Jordan stored, as they wrote it."
                                )}
                            </p>
                        </div>
                        <ExampleResume view={tailored ? "tailored" : "original"} />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="px-1.5">
                        <Step n={3}>The application, filled</Step>
                    </div>
                    <ExampleForm mode={mode} />
                </div>
            </div>
        </PlacePreview>
    );
}
